import toast from 'react-hot-toast';

import Request from '@/services/util/request-service';
import LocalStorageService from '@/services/storage/general/local-storage-service';
import { CourseData, CourseId, UserCoursesCookie } from 'course-types';
import UserProgressStorageService from '@/services/storage/user-progress-storage-service';
import ProgressUtilityService from '@/services/course/progress-utility-service';

const UserCourseStorageService = {
  getCourseCookie(): UserCoursesCookie | undefined {
    return LocalStorageService.get('U_COURSE') as unknown as UserCoursesCookie;
  },

  async retrieveCourseCookie(): Promise<UserCoursesCookie> {
    const userCourses = await (await Request.get(`user/getUserCourse`))?.json();
    if (!userCourses?.userCourses) {
      toast.error('Failed to retrieve user course data');
      return { active: '', all: { user: [], group: {}, enterprise: [] } };
    }

    const data: UserCoursesCookie = { active: '', all: userCourses.userCourses };
    this.setCourseCookie(data);
    return data;
  },

  setCourseCookie(data: UserCoursesCookie): void {
    LocalStorageService.set('U_COURSE', data);
    return;
  },

  resetCourseCookie(): void {
    LocalStorageService.delete('U_COURSE');
    return;
  },

  getCourseData(): CourseData | undefined {
    return LocalStorageService.get('course-map') as CourseData;
  },

  async setCourseData(id: string): Promise<CourseData> {
    const json = await (await Request.get(`course/getCourse/${id}`))?.json();
    let courseData = (json?.data ?? {}) as CourseData;

    const hasUnits =
      courseData &&
      typeof (courseData as any).units === 'object' &&
      (courseData as any).units !== null;

    if (!hasUnits) {
      courseData = {
        id: id as CourseId,
        name: (courseData as any)?.name ?? 'Error Loading Course',
        image: (courseData as any)?.image ?? null,
        units: {},
      } as CourseData;
    } else {
      (courseData as any).id = id as CourseId;
    }

    LocalStorageService.set('course-map', courseData);

    try {
      await UserProgressStorageService.initCourseProgress(courseData);
    } catch (e) {
      console.warn('initCourseProgress failed:', e);
    }

    await ProgressUtilityService.cleanupProgressData(courseData);

    return courseData;
  },

  resetCourseData(): void {
    LocalStorageService.delete('course-map');
    return;
  },
};

export default UserCourseStorageService;
